// Libraries
import { QueryClient } from '@tanstack/react-query';
import { gcApi } from 'config/axios';

// API Util
import { useSpecificQuery, useSpecificMutation } from 'services/util/queryUtil';

// Constants
const baseUrl = '/reportdashboard-service';
export const advancedReportingConfigBaseKey = 'AdvancedReportingConfig';

// QueryClient
export const advancedReportingConfigClient = new QueryClient();

export const allDashboardsKey = 'AllDashboards';

// Enums
export const GROUPING_TYPE = {
    DEFAULT: null,
    ACTIVITY_TYPE: 'summary/activitytype'
};

export const OVERLAY_TYPE = {
    DEFAULT: null,
    PERFORMANCE: 'performance'
};

export const TIME_RANGE_TYPE = {
    DEFAULT: null,
    COMPARISON: 'comparison',
    COMPARISON_POWER_CURVE: 'comparison/powercurve'
};

// Base TanStack types

// Options
/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').UndefinedSpecificInitialDataOptions<TData>} UndefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').DefinedSpecificInitialDataOptions<TData>} DefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @typedef {import('utils/services/queryUtil'). UndefinedSpecificInitialDataInfiniteOptions<TData>} UndefinedSpecificInitialDataInfiniteOptions
 */

/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').DefinedSpecificInitialDataInfiniteOptions<TData>} DefinedSpecificInitialDataInfiniteOptions
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationOptions<TData, TError, TVariables, TContext>} UseMutationOptions
 */

// Results
/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseQueryResult<TData, TError>} UseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<TData, TError>} DefinedUseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseInfiniteQueryResult<TData, TError>} UseInfiniteQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseInfiniteQueryResult<TData, TError>} DefinedUseInfiniteQueryResult
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationResult<TData, TError, TVariables, TContext>} UseMutationResult
 */

// Custom types
/**
 * @typedef {import('services/gcApi/reportDashboardService/schemas/config/Chart')} Chart
 * @typedef {import('services/gcApi/reportDashboardService/schemas/config/ChartDefinition')} ChartDefinition
 * @typedef {import('services/gcApi/reportDashboardService/schemas/config/ChartConfig')} ChartConfig
 * @typedef {import('services/gcApi/reportDashboardService/schemas/config/ChartTimeRangeConfig')} ChartTimeRangeConfig
 * @typedef {import('services/gcApi/reportDashboardService/schemas/config/DashboardConfig')} DashboardConfig
 */

// Charts //

// Queries

/**
 * Get a chart by its ID.
 *
 * @param {{ chartId: string }} parameters
 * @param {UndefinedSpecificInitialDataOptions<Chart> | DefinedSpecificInitialDataOptions<Chart> =} queryParams
 * @returns {UseQueryResult<Chart> | DefinedUseQueryResult<Chart>}
 */
export const useGetChart = ({ chartId }, queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'Chart', chartId],
            queryFn: async () => (await gcApi.get(`${baseUrl}/chart/${chartId}`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get all available chart definitions.
 *
 * @param {UndefinedSpecificInitialDataOptions<ChartDefinition> | DefinedSpecificInitialDataOptions<ChartDefinition> =} queryParams
 * @returns {UseQueryResult<ChartDefinition> | DefinedUseQueryResult<ChartDefinition>}
 */
export const useGetChartDefinitions = (queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            staleTime: Infinity,
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'ChartDefinitions'],
            queryFn: async () => (await gcApi.get(`${baseUrl}/chart/meta`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get all available options for customizing the x-axis group by of compatible charts.
 *
 * @param {{ type: string | undefined }} parameters
 * @param {UndefinedSpecificInitialDataOptions<string[]> | DefinedSpecificInitialDataOptions<string[]> =} queryParams
 * @returns {UseQueryResult<string[]> | DefinedUseQueryResult<string[]>}
 */
export const useGetChartGroupingOptions = (
    { type = undefined } = { type: undefined },
    queryParams = { enabled: true }
) => {
    let typeKey = type;
    switch (type) {
        default:
        case GROUPING_TYPE.DEFAULT:
            typeKey = 'default';
            break;
        case GROUPING_TYPE.ACTIVITY_TYPE:
            break;
    }

    return useSpecificQuery(
        {
            staleTime: Infinity,
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'ChartOptions', 'Grouping', typeKey],
            queryFn: async () => (await gcApi.get(`${baseUrl}/chart/meta/groupby${type ? `/${type}` : ''}`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get a list of metrics available as metric overlays on charts.
 *
 * @param {{ type: string | undefined }} parameters
 * @param {UndefinedSpecificInitialDataOptions<string[]> | DefinedSpecificInitialDataOptions<string[]> =} queryParams
 * @returns {UseQueryResult<string[]> | DefinedUseQueryResult<string[]>}
 */
export const useGetChartMetricOverlayOptions = (
    { type = undefined } = { type: undefined },
    queryParams = { enabled: true }
) => {
    let typeKey = type;
    switch (type) {
        default:
        case OVERLAY_TYPE.DEFAULT:
            typeKey = 'default';
            break;
        case OVERLAY_TYPE.PERFORMANCE:
            break;
    }

    return useSpecificQuery(
        {
            staleTime: Infinity,
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'ChartOptions', 'MetricOverlay', typeKey],
            queryFn: async () => (await gcApi.get(`${baseUrl}/chart/meta/overlay${type ? `/${type}` : ''}`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get all available options for chart/dashboard time range filters.
 *
 * @param {{ type: string | undefined }} parameters
 * @param {UndefinedSpecificInitialDataOptions<string[]> | DefinedSpecificInitialDataOptions<string[]> =} queryParams
 * @returns {UseQueryResult<string[]> | DefinedUseQueryResult<string[]>}
 */
export const useGetChartTimeRangeOptions = (
    { type = undefined } = { type: undefined },
    queryParams = { enabled: true }
) => {
    let typeKey = type;
    switch (type) {
        default:
        case TIME_RANGE_TYPE.DEFAULT:
            typeKey = 'default';
            break;
        case TIME_RANGE_TYPE.COMPARISON:
        case TIME_RANGE_TYPE.COMPARISON_POWER_CURVE:
            break;
    }

    return useSpecificQuery(
        {
            staleTime: Infinity,
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'ChartOptions', 'TimeRange', typeKey],
            queryFn: async () => (await gcApi.get(`${baseUrl}/chart/meta/timerange${type ? `/${type}` : ''}`)).data
        },
        advancedReportingConfigClient
    );
};

// Mutations

/**
 * @typedef ChartConfigUpdateVariables
 * @property {string} chartId
 * @property {ChartConfig} config
 */

/**
 * Update local configs for a chart by its ID.
 * The provided configuration will replace the existing configuration.
 *
 * @param {UseMutationOptions<null, ChartConfigUpdateVariables> =} mutationParams
 * @returns {UseMutationResult<null, ChartConfigUpdateVariables>}
 */
export const useUpdateChart = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Chart'],
            mutationFn: async ({ chartId, config }) =>
                (await gcApi.put(`${baseUrl}/chart/config/${chartId}`, config)).data
        },
        advancedReportingConfigClient
    );
};

// Dashboards //

// Queries

/**
 * Get all dashboards for the currently authenticated user.
 *
 * @param {UndefinedSpecificInitialDataOptions<DashboardConfig[]> | DefinedSpecificInitialDataOptions<DashboardConfig[]> =} queryParams
 * @returns {UseQueryResult<DashboardConfig[]> | DefinedUseQueryResult<DashboardConfig[]>}
 */
export const useGetAllDashboards = (queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, allDashboardsKey],
            queryFn: async () => (await gcApi.get(`${baseUrl}/dashboard`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get a dashboard by its ID.
 *
 * @param {{ dashboardId: string }} parameters
 * @param {UndefinedSpecificInitialDataOptions<DashboardConfig> | DefinedSpecificInitialDataOptions<DashboardConfig> =} queryParams
 * @returns {UseQueryResult<DashboardConfig> | DefinedUseQueryResult<DashboardConfig>}
 */
export const useGetDashboard = ({ dashboardId }, queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'Dashboard', dashboardId],
            queryFn: async () => (await gcApi.get(`${baseUrl}/dashboard/${dashboardId}`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get all available profile values for prebuilt dashboards.
 *
 * @param {UndefinedSpecificInitialDataOptions<string[]> | DefinedSpecificInitialDataOptions<string[]> =} queryParams
 * @returns {UseQueryResult<string[]> | DefinedUseQueryResult<string[]>}
 */
export const useGetDashboardProfiles = (queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'DashboardProfiles'],
            queryFn: async () => (await gcApi.get(`${baseUrl}/dashboard/meta/profile`)).data
        },
        advancedReportingConfigClient
    );
};

/**
 * Get all limits for the feature.
 *
 * @param {UndefinedSpecificInitialDataOptions<string[]> | DefinedSpecificInitialDataOptions<string[]> =} queryParams
 * @returns {UseQueryResult<string[]> | DefinedUseQueryResult<string[]>}
 */
export const useGetDashboardMetaLimits = (queryParams = { enabled: true }) => {
    return useSpecificQuery(
        {
            staleTime: Infinity,
            ...(queryParams || {}),
            queryKey: [advancedReportingConfigBaseKey, 'DashboardMetaLimits'],
            queryFn: async () => (await gcApi.get(`${baseUrl}/dashboard/meta/limits`)).data
        },
        advancedReportingConfigClient
    );
};

// Mutations

/**
 * @typedef DashboardCreateFromProfileVariables
 * @property {string} profile
 * @property {string} dashboardName
 */

/**
 * Create a new dashboard based on a predefined profile.
 * The response will contain what was created for the user.
 *
 * @param {UseMutationOptions<DashboardConfig, DashboardCreateFromProfileVariables> =} mutationParams
 * @returns {UseMutationResult<DashboardConfig, DashboardCreateFromProfileVariables>}
 */
export const useCreateDashboardFromProfile = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'CreateFromProfile'],
            mutationFn: async ({ profile, dashboardName }) =>
                (
                    await gcApi.post(`${baseUrl}/dashboard/${profile}`, {
                        dashboardName: dashboardName
                    })
                ).data,
            onSuccess: (data, variables, context) => {
                // Update list of dashboards
                advancedReportingConfigClient.setQueryData(
                    [advancedReportingConfigBaseKey, allDashboardsKey],
                    /**
                     * @param {DashboardConfig[] =} oldAllDashboards
                     * @returns {DashboardConfig[] =}
                     */
                    (oldAllDashboards) => {
                        const newAllDashboards = oldAllDashboards?.length ? structuredClone(oldAllDashboards) : [];
                        newAllDashboards.push(data);
                        return newAllDashboards;
                    }
                );

                // Call passed in onSuccess from hook (if it exists)
                if (mutationParams?.onSuccess) mutationParams.onSuccess(data, variables, context);
            }
        },
        advancedReportingConfigClient
    );
};

/**
 * @typedef DashboardCreateVariables
 * @property {DashboardConfig} config
 */

/**
 * Create a new dashboard.
 * The response will contain what was created for the user.
 *
 * @param {UseMutationOptions<DashboardConfig, DashboardCreateVariables> =} mutationParams
 * @returns {UseMutationResult<DashboardConfig, DashboardCreateVariables>}
 */
export const useCreateDashboard = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'Create'],
            mutationFn: async ({ config }) => (await gcApi.post(`${baseUrl}/dashboard`, config)).data,
            onSuccess: (data, variables, context) => {
                // Update list of dashboards
                advancedReportingConfigClient.setQueryData(
                    [advancedReportingConfigBaseKey, allDashboardsKey],
                    /**
                     * @param {DashboardConfig[] =} oldAllDashboards
                     * @returns {DashboardConfig[] =}
                     */
                    (oldAllDashboards) => {
                        const newAllDashboards = oldAllDashboards?.length ? structuredClone(oldAllDashboards) : [];
                        newAllDashboards.push(data);
                        return newAllDashboards;
                    }
                );

                // Call passed in onSuccess from hook (if it exists)
                if (mutationParams?.onSuccess) mutationParams.onSuccess(data, variables, context);
            }
        },
        advancedReportingConfigClient
    );
};

/**
 * @typedef DashboardDeleteVariables
 * @property {string} dashboardId
 */

/**
 * Delete the specified dashboard.
 *
 * @param {UseMutationOptions<null, DashboardDeleteVariables> =} mutationParams
 * @returns {UseMutationResult<null, DashboardDeleteVariables>}
 */
export const useDeleteDashboard = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'Delete'],
            mutationFn: async ({ dashboardId }) => (await gcApi.delete(`${baseUrl}/dashboard/${dashboardId}`)).data,
            onSuccess: (data, variables, context) => {
                // Call passed in onSuccess from hook (if it exists)
                // Unlike in other places, we do any passed in onSuccess calls first so that
                // the deleted dashboard can still be accessed before being removed from the list.
                if (mutationParams?.onSuccess) mutationParams.onSuccess(data, variables, context);

                // Update list of dashboards
                advancedReportingConfigClient.setQueryData(
                    [advancedReportingConfigBaseKey, allDashboardsKey],
                    /**
                     * @param {DashboardConfig[] =} oldAllDashboards
                     * @returns {DashboardConfig[] =}
                     */
                    (oldAllDashboards) => {
                        // Do nothing if no dashboards loaded
                        if (!oldAllDashboards?.length) return undefined;

                        // Get index of deleted dashboard and delete it
                        const deletedIndex = oldAllDashboards.findIndex(
                            (dashboard) => dashboard.dashboardID.uuid === variables.dashboardId
                        );
                        if (deletedIndex === -1) return undefined;

                        const newAllDashboards = structuredClone(oldAllDashboards);
                        newAllDashboards.splice(deletedIndex, 1);
                        return newAllDashboards;
                    }
                );
            }
        },
        advancedReportingConfigClient
    );
};

/**
 * @typedef DashboardUpdateVariables
 * @property {string} dashboardId
 * @property {DashboardConfig} config
 */

/**
 * Updates the specified dashboard.
 * The definition provided will overwrite the specified dashboard.
 * The response will contain the updated dashboard configuration.
 *
 * The dashboard name and time range configuration will be updated along with any changes to charts on the dashboard.
 *
 *
 * Dashboard charts that contain a chart ID will be updated, those missing chart IDs will be deleted, and those that do not have a chart ID will be added.
 *
 * @param {UseMutationOptions<DashboardConfig, DashboardUpdateVariables> =} mutationParams
 * @returns {UseMutationResult<DashboardConfig, DashboardUpdateVariables>}
 */
export const useUpdateDashboard = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'Update', 'Config'],
            mutationFn: async ({ dashboardId, config }) =>
                (await gcApi.put(`${baseUrl}/dashboard/${dashboardId}`, config)).data,
            onSuccess: (data, variables, context) => {
                // Update list of dashboards
                advancedReportingConfigClient.setQueryData(
                    [advancedReportingConfigBaseKey, allDashboardsKey],
                    /**
                     * @param {DashboardConfig[] =} oldAllDashboards
                     * @returns {DashboardConfig[] =}
                     */
                    (oldAllDashboards) => {
                        // Do nothing if no dashboards loaded
                        if (!oldAllDashboards?.length) return undefined;

                        // Get index of edited dashboard and set the name
                        const editedIndex = oldAllDashboards.findIndex(
                            (dashboard) => dashboard.dashboardID.uuid === data.dashboardID.uuid
                        );
                        if (editedIndex === -1 || oldAllDashboards[editedIndex].dashboardName === data.dashboardName)
                            return undefined;

                        const newAllDashboards = structuredClone(oldAllDashboards);
                        newAllDashboards[editedIndex].dashboardName = data.dashboardName;
                        return newAllDashboards;
                    }
                );

                // Call passed in onSuccess from hook (if it exists)
                if (mutationParams?.onSuccess) mutationParams.onSuccess(data, variables, context);
            }
        },
        advancedReportingConfigClient
    );
};

/**
 * @typedef DashboardUpdateNameVariables
 * @property {string} dashboardId
 * @property {string} name
 */

/**
 * Updates the specified dashboard's name.
 *
 * @param {UseMutationOptions<null, DashboardUpdateNameVariables> =} mutationParams
 * @returns {UseMutationResult<null, DashboardUpdateNameVariables>}
 */
export const useUpdateDashboardName = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'Update', 'Name'],
            mutationFn: async ({ dashboardId, name }) =>
                (
                    await gcApi.put(`${baseUrl}/dashboard/config/name/${dashboardId}`, `${name}`, {
                        headers: { 'Content-Type': 'application/json' }
                    })
                ).data,
            onSuccess: (data, variables, context) => {
                // Update list of dashboards
                advancedReportingConfigClient.setQueryData(
                    [advancedReportingConfigBaseKey, allDashboardsKey],
                    /**
                     * @param {DashboardConfig[] =} oldAllDashboards
                     * @returns {DashboardConfig[] =}
                     */
                    (oldAllDashboards) => {
                        // Do nothing if no dashboards loaded
                        if (!oldAllDashboards?.length) return undefined;

                        // Get index of renamed dashboard and set the name
                        const renamedIndex = oldAllDashboards.findIndex(
                            (dashboard) => dashboard.dashboardID.uuid === variables.dashboardId
                        );
                        if (renamedIndex === -1) return undefined;

                        const newAllDashboards = structuredClone(oldAllDashboards);
                        newAllDashboards[renamedIndex].dashboardName = variables.name;
                        return newAllDashboards;
                    }
                );

                // Call passed in onSuccess from hook (if it exists)
                if (mutationParams?.onSuccess) mutationParams.onSuccess(data, variables, context);
            }
        },
        advancedReportingConfigClient
    );
};

/**
 * @typedef DashboardUpdateTimeRangeVariables
 * @property {string} dashboardId
 * @property {ChartTimeRangeConfig} timeRange
 */

/**
 * Updates the specified dashboard's time range.
 *
 * @param {UseMutationOptions<null, DashboardUpdateTimeRangeVariables> =} mutationParams
 * @returns {UseMutationResult<null, DashboardUpdateTimeRangeVariables>}
 */
export const useUpdateDashboardTimeRange = (mutationParams = {}) => {
    return useSpecificMutation(
        {
            ...(mutationParams || {}),
            mutationKey: [advancedReportingConfigBaseKey, 'Dashboard', 'Update', 'TimeRange'],
            mutationFn: async ({ dashboardId, timeRange }) =>
                (await gcApi.put(`${baseUrl}/dashboard/config/timerange/${dashboardId}`, timeRange)).data
        },
        advancedReportingConfigClient
    );
};
